import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  saveSignLine,
  signLineList,
  getSignLineInfo,
  signLineModify,
  signLineRemove,
} from '@entities/signLine/api/signLine';
/**
 * 결재라인 저장 Mutation
 *
 * @param {Object} [options] - useMutation 옵션 (onSuccess, onError 등)
 * @returns {import('@tanstack/react-query').UseMutationResult} Mutation 객체
 */
function useSaveSignLineMutation(options = {}) {
  return useMutation({
    mutationFn: ({ enumber, company, title, column, values }) =>
      saveSignLine({
        enumber,
        company,
        title,
        column,
        values,
      }),
    ...options,
  });
}
/**
 * 결재라인 리스트
 */
function useSignLineListQuery(options = {}) {
  return useQuery({
    queryKey: ['signList'],
    queryFn: signLineList,
    // 결재라인 목록은 자주 변경되지 않으므로 10분간 캐싱
    staleTime: 1000 * 60 * 10,
    ...options,
  });
}

/**
 * 결재라인 상세 조회 Query
 *
 * @param {number} signLineId - 결재라인 ID
 * @param {Object} options - react-query 옵션 (enabled, staleTime 등)
 */
function useSignLineInfoQuery(signLineId, options = {}) {
  return useQuery({
    queryKey: ['signLineInfo', signLineId],
    queryFn: () => getSignLineInfo(signLineId),
    // 결재라인 상세 정보는 자주 변경되지 않으므로 10분간 캐싱
    staleTime: 1000 * 60 * 10,
    ...options,
  });
}

/**
 * 결재라인 수정 Mutation
 */
function useSignLineModify(options = {}) {
  return useMutation({
    mutationFn: ({ idx, setValue }) =>
      signLineModify({
        idx,
        setValue,
      }),
    ...options,
  });
}

/**
 * 결재라인 삭제
 * 삭제 성공 시 signList 쿼리를 무효화하여 목록이 즉시 리패치되도록 함
 */
function useSignLineRemoveMutation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ idx }) => signLineRemove({ idx }),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['signList'] });
      options.onSuccess?.(data, variables, context);
    },
  });
}
export {
  useSaveSignLineMutation,
  useSignLineListQuery,
  useSignLineInfoQuery,
  useSignLineModify,
  useSignLineRemoveMutation,
};
